import { api } from './_client';
import type {
  ManufacturingSyncStatus,
  ManufacturingProduct,
  ManufacturingWorkCenter,
  BomWithLines,
  ProductionOrderWithWorkOrders,
  OnshapeDocument,
  DriftReport,
  PartDetail,
  OnshapePart,
  PartsByCodeResponse,
  SimulationRequest,
  SimulationReport,
} from '../../types/manufacturing';

export const manufacturingApi = {
  async checkManufacturingAccess(): Promise<{ has_access: boolean }> {
    const response = await api.get<{ has_access: boolean }>('/api/manufacturing/access');
    return response.data;
  },

  async getManufacturingSyncStatus(): Promise<ManufacturingSyncStatus> {
    const response = await api.get<ManufacturingSyncStatus>('/api/manufacturing/sync-status');
    return response.data;
  },

  async getManufacturingProducts(): Promise<ManufacturingProduct[]> {
    const response = await api.get<ManufacturingProduct[]>('/api/manufacturing/products');
    return response.data;
  },

  async getManufacturingWorkCenters(): Promise<ManufacturingWorkCenter[]> {
    const response = await api.get<ManufacturingWorkCenter[]>('/api/manufacturing/work-centers');
    return response.data;
  },

  async getManufacturingBoms(): Promise<BomWithLines[]> {
    const response = await api.get<BomWithLines[]>('/api/manufacturing/boms');
    return response.data;
  },

  async getManufacturingProductionOrders(): Promise<ProductionOrderWithWorkOrders[]> {
    const response = await api.get<ProductionOrderWithWorkOrders[]>(
      '/api/manufacturing/production-orders'
    );
    return response.data;
  },

  async getManufacturingOnshapeDocuments(): Promise<OnshapeDocument[]> {
    const response = await api.get<OnshapeDocument[]>('/api/manufacturing/onshape/documents');
    return response.data;
  },

  async getManufacturingDrift(): Promise<DriftReport> {
    const response = await api.get<DriftReport>('/api/manufacturing/drift');
    return response.data;
  },

  async getManufacturingPartDetail(partId: string): Promise<PartDetail> {
    const response = await api.get<PartDetail>(
      `/api/manufacturing/parts/${encodeURIComponent(partId)}/detail`
    );
    return response.data;
  },

  async getManufacturingOnshapeParts(): Promise<OnshapePart[]> {
    const response = await api.get<OnshapePart[]>('/api/manufacturing/onshape/parts');
    return response.data;
  },

  /**
   * Batched Odoo default_code -> Onshape part + work-instruction lookup.
   *
   * Replaces the old substring-on-document-name heuristic in BomTreeExplore
   * with the same exact-equality join the drift view uses
   * (product_variants.default_code == onshape.parts.part_number).
   *
   * Codes that don't resolve are simply absent from the response. The FE
   * should default to "no chip" for those nodes.
   *
   * Codes are deduplicated and bounded to 500 by the backend; callers should
   * filter null/empty codes before sending.
   */
  async getManufacturingOnshapePartsByCode(codes: string[]): Promise<PartsByCodeResponse> {
    if (codes.length === 0) return {};
    const response = await api.get<PartsByCodeResponse>(
      '/api/manufacturing/onshape/parts-by-code',
      {
        params: { codes: codes.join(',') },
      }
    );
    return response.data;
  },

  /**
   * Fetch a backend-proxied Onshape drawing element as a viewable PDF blob.
   *
   * Goes through the authenticated axios client so the request lands on the
   * real API origin (`VITE_API_URL`) WITH the Supabase JWT attached. The proxy
   * validates the element exists in the mirror with the correct type, then
   * translates + caches the PDF (the first, uncached call can take well past
   * the default 10 s timeout; subsequent calls hit the 24 h S3 cache).
   *
   * Do NOT instead build a bare `/api/...` string and `window.open` it: that
   * resolves against the SPA origin (not the backend) and carries no auth, so
   * the new tab lands on the dashboard SPA instead of the PDF. The caller turns
   * this blob into an object URL and opens it. (Replaces the old
   * `getManufacturingDrawingProxyUrl` URL builder — see the #1067 follow-up.)
   */
  async downloadManufacturingDrawing(drawingElementId: string): Promise<Blob> {
    // Path-component encoding even though the backend re-validates as 24-hex:
    // defense-in-depth against a hypothetical caller that passes an
    // attacker-controlled string through (none today; the value originates
    // from getManufacturingOnshapePartsByCode which the server controls).
    const response = await api.get<Blob>(
      `/api/manufacturing/onshape/drawing/${encodeURIComponent(drawingElementId)}`,
      {
        responseType: 'blob',
        timeout: 60000, // first uncached Onshape translation can take ~10-30 s
      }
    );
    return response.data;
  },

  async getManufacturingSimulation(request: SimulationRequest): Promise<SimulationReport> {
    const response = await api.post<SimulationReport>('/api/manufacturing/simulate', request);
    return response.data;
  },
};
